import React, { useEffect, useMemo, useState } from "react";
import { listSeekers } from "../../data/seekersService";
import {
  Seeker,
  SeekerListResult,
  SeekerStatus,
  OnboardingStatus,
} from "../../types/seeker";
import SeekerStatusBadge from "../../components/SeekerStatusBadge";
import OnboardingStatusBadge from "../../components/OnboardingStatusBadge";
import SeekerStats from "../../components/SeekerStats";
import SeekerDetailModal from "./SeekerDetailModal";
import DeleteUserModal from "../../components/DeleteUserModal";
import { getSupabaseClient } from "../../lib/supabase";
import { useZipLocation } from "../../hooks/useZipLocation";

const STATUS_OPTIONS: Array<SeekerStatus | "all"> = [
  "all",
  "new",
  "active",
  "inactive",
  "no_match",
];

const ONBOARDING_STATUS_OPTIONS: Array<OnboardingStatus | "all"> = [
  "all",
  "in_progress",
  "basic_info_completed",
  "experience_preferences_completed",
  "completed",
  "consultation_scheduled",
  // GCS-214: post-consultation / package lifecycle states
  "consultation_completed",
  "consultation_missed",
  "awaiting_package",
  "package_purchased",
  "package_delivered",
  "package_complete",
];

// GCS-214: human labels for the filter dropdown.
const ONBOARDING_STATUS_LABELS: Record<OnboardingStatus | "all", string> = {
  all: "All onboarding statuses",
  in_progress: "In progress",
  basic_info_completed: "Basic info",
  experience_preferences_completed: "Experience preferences",
  completed: "Completed",
  consultation_scheduled: "Consultation Scheduled",
  consultation_completed: "Consultation Call Complete",
  consultation_missed: "Consultation Call Missed",
  awaiting_package: "Awaiting Package",
  package_purchased: "Package Purchased",
  package_delivered: "Package Delivered",
  package_complete: "Package Complete",
};

type SortField = "name" | "status" | "onboardingStatus" | "connectedGuides" | "lifetimeValue" | "joinedAt";
type SortDirection = "asc" | "desc";

function SeekerLocationCell({ postcode }: { postcode?: string }) {
  const location = useZipLocation(postcode, "—");
  return <>{location}</>;
}

export default function SeekerListPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SeekerStatus | "all">("all");
  const [onboardingStatus, setOnboardingStatus] =
    useState<OnboardingStatus | "all">("all");
  const [lifetimeMin, setLifetimeMin] = useState("");
  const [lifetimeMax, setLifetimeMax] = useState("");

  const [showFilters, setShowFilters] = useState(false);
  const [draftStatus, setDraftStatus] = useState<SeekerStatus | "all">("all");
  const [draftOnboardingStatus, setDraftOnboardingStatus] =
    useState<OnboardingStatus | "all">("all");
  const [draftLifetimeMin, setDraftLifetimeMin] = useState("");
  const [draftLifetimeMax, setDraftLifetimeMax] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  const [res, setRes] = useState<SeekerListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSeeker, setSelectedSeeker] = useState<Seeker | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Seeker | null>(null);

  const [sortField, setSortField] = useState<SortField>("joinedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const totalPages = useMemo(() => {
    if (!res) return 1;
    return Math.max(1, Math.ceil(res.total / pageSize));
  }, [res, pageSize]);

  // No client-side sorting - sorting is done on the backend
  const sortedItems = res?.items ?? [];

  const allSelected = sortedItems.length > 0 && sortedItems.every(s => selectedIds.has(s.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  useEffect(() => {
    setLoading(true);
    listSeekers({
      q,
      status,
      onboardingStatus,
      lifetimeValueMin: lifetimeMin ? Number(lifetimeMin) : undefined,
      lifetimeValueMax: lifetimeMax ? Number(lifetimeMax) : undefined,
      page,
      pageSize,
      sortBy: sortField,
      sortDirection,
    })
      .then(setRes)
      .finally(() => setLoading(false));
  }, [q, status, onboardingStatus, lifetimeMin, lifetimeMax, page, pageSize, sortField, sortDirection]);

  function refreshSamePage() {
    setLoading(true);
    listSeekers({
      q,
      status,
      onboardingStatus,
      lifetimeValueMin: lifetimeMin ? Number(lifetimeMin) : undefined,
      lifetimeValueMax: lifetimeMax ? Number(lifetimeMax) : undefined,
      page,
      pageSize,
      sortBy: sortField,
      sortDirection,
    })
      .then(setRes)
      .finally(() => setLoading(false));
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedItems.map(s => s.id)));
    }
  }

  function toggleSelect(id: string) {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  }

  function handleExportTable() {
    const selectedSeekers = sortedItems.filter(s => selectedIds.has(s.id));
    const csv = [
      ["Name", "Email", "Phone", "Status", "Onboarding Status", "Connected Guides", "Lifetime Spend", "Joined"].join(","),
      ...selectedSeekers.map(s => [
        s.name || "",
        s.email,
        s.phone || "",
        s.status,
        s.onboardingStatus,
        s.connectedGuides,
        s.lifetimeValue,
        new Date(s.joinedAt).toLocaleDateString(),
      ].join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `seekers-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowBulkActions(false);
  }

  async function handleExportAllData() {
    const selectedSeekers = sortedItems.filter(s => selectedIds.has(s.id));

    if (selectedSeekers.length === 0) {
      alert('No seekers selected');
      return;
    }

    setShowBulkActions(false);

    // Show loading indicator
    const loadingMsg = `Exporting data for ${selectedSeekers.length} seeker${selectedSeekers.length > 1 ? 's' : ''}...`;
    console.log(loadingMsg);

    const supabase = getSupabaseClient();

    for (const seeker of selectedSeekers) {
      try {
        // Fetch comprehensive data for this seeker
        const userId = seeker.userId;

        // Fetch onboarding data
        const { data: onboardingData } = await supabase
          .from('seeker_onboarding_complete')
          .select('*')
          .eq('seeker_id', userId)
          .maybeSingle();

        // Fetch active match
        const { data: activeMatch } = await supabase
          .from('matches')
          .select(`
            *,
            change_request:match_change_requests(
              id,
              initiator,
              reason_id,
              reason_label,
              details,
              status,
              created_at,
              decided_at
            ),
            guide_details:guide_id(
              user_id,
              status
            )
          `)
          .eq('seeker_id', userId)
          .or('status.eq.active,status.is.null')
          .maybeSingle();

        // Fetch match history
        const { data: matchHistory } = await supabase
          .from('matches')
          .select(`
            *,
            change_request:match_change_requests(
              id,
              initiator,
              reason_id,
              reason_label,
              details,
              status,
              created_at,
              decided_at
            )
          `)
          .eq('seeker_id', userId)
          .order('created_at', { ascending: false });

        // Fetch sessions
        const { data: sessions } = await supabase
          .from('sessions')
          .select('*')
          .eq('seeker_id', userId)
          .order('session_date', { ascending: false });

        // Fetch profile data for additional info
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, phone, email, seeker_initiated_changes, guide_initiated_changes')
          .eq('id', userId)
          .maybeSingle();

        // Build export payload
        const basicSeekerInfo = {
          id: seeker.id,
          userId: userId ?? null,
          name: profileData?.full_name ?? onboardingData?.seeker_full_name ?? seeker.name,
          email: profileData?.email ?? onboardingData?.seeker_email ?? seeker.email,
          phone: profileData?.phone ?? onboardingData?.seeker_phone ?? seeker.phone ?? null,
          status: seeker.status,
          connectedGuides: seeker.connectedGuides,
          lifetimeValue: seeker.lifetimeValue,
          joinedAt: seeker.joinedAt,
          seekerInitiatedChanges: profileData?.seeker_initiated_changes ?? null,
          guideInitiatedChanges: profileData?.guide_initiated_changes ?? null,
        };

        const onboardingInfo = onboardingData ? {
          sessionId: onboardingData.session_id ?? null,
          seekerOver18: onboardingData.seeker_over_18 ?? null,
          locationPostcode: onboardingData.location_postcode ?? null,
          seekerGender: onboardingData.seeker_gender ?? null,
          experienceTypes: onboardingData.experience_types ?? null,
          medicineOptionsComfort: onboardingData.medicine_options_comfort ?? null,
          supportTypes: onboardingData.support_types ?? null,
          travelWillingness: onboardingData.travel_willingness ?? null,
          travelDistance: onboardingData.travel_distance ?? null,
          focusPreferences: onboardingData.focus_preferences ?? null,
          preferSpecialistInGroups: onboardingData.prefer_specialist_in_groups ?? null,
          religionAlignmentImportant: onboardingData.religion_alignment_important ?? null,
          seekerBeliefs: onboardingData.seeker_beliefs ?? null,
          currentlyOnMeds: onboardingData.currently_on_meds ?? null,
          clinicalDiagnoses: onboardingData.clinical_diagnoses ?? null,
          budgetBand: onboardingData.budget_band ?? null,
          additionalInfo: onboardingData.additional_info ?? null,
          onboardingStartedAt: onboardingData.created_at ?? null,
          onboardingCompletedAt: onboardingData.updated_at ?? null,
          onboardingStatus: seeker.onboardingStatus ?? null,
        } : null;

        const matchedGuide = activeMatch ? {
          guideId: activeMatch.guide_id,
          matchScore: activeMatch.match_score,
          scoreBreakdown: activeMatch.score_breakdown,
          reasoning: activeMatch.reasoning,
          createdAt: activeMatch.created_at,
          updatedAt: activeMatch.updated_at ?? null,
          status: activeMatch.status ?? null,
          endedAt: activeMatch.ended_at ?? null,
          changeRequest: activeMatch.change_request ? {
            id: activeMatch.change_request.id,
            initiator: activeMatch.change_request.initiator,
            reasonId: activeMatch.change_request.reason_id ?? null,
            reasonLabel: activeMatch.change_request.reason_label ?? null,
            details: activeMatch.change_request.details ?? null,
            status: activeMatch.change_request.status ?? null,
            createdAt: activeMatch.change_request.created_at ?? null,
            decidedAt: activeMatch.change_request.decided_at ?? null,
          } : null,
          guideDetails: activeMatch.guide_details ?? null,
        } : null;

        const matchHistoryExport = (matchHistory ?? []).map((historyMatch) => ({
          id: historyMatch.id,
          guideId: historyMatch.guide_id,
          status: historyMatch.status ?? null,
          createdAt: historyMatch.created_at,
          updatedAt: historyMatch.updated_at ?? null,
          endedAt: historyMatch.ended_at ?? null,
          matchScore: historyMatch.match_score,
          scoreBreakdown: historyMatch.score_breakdown,
          reasoning: historyMatch.reasoning,
          changeRequest: historyMatch.change_request ? {
            id: historyMatch.change_request.id,
            initiator: historyMatch.change_request.initiator,
            reasonId: historyMatch.change_request.reason_id ?? null,
            reasonLabel: historyMatch.change_request.reason_label ?? null,
            details: historyMatch.change_request.details ?? null,
            status: historyMatch.change_request.status ?? null,
            createdAt: historyMatch.change_request.created_at ?? null,
            decidedAt: historyMatch.change_request.decided_at ?? null,
          } : null,
        }));

        const sessionData = (sessions ?? []).map((session) => ({
          id: session.id,
          guideId: session.guide_id,
          seekerId: session.seeker_id,
          sessionType: session.session_type,
          sessionDate: session.session_date,
          sessionTime: session.session_time,
          durationMinutes: session.duration_minutes,
          sessionStatus: session.session_status,
          notes: session.notes,
          createdAt: session.created_at,
          updatedAt: session.updated_at,
        }));

        const exportPayload = {
          generatedAt: new Date().toISOString(),
          seeker: basicSeekerInfo,
          onboarding: onboardingInfo,
          matchedGuide,
          sessions: sessionData,
          matchHistory: matchHistoryExport,
        };

        // Download JSON file for this seeker
        const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        const safeName = seeker.name
          ? seeker.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
          : 'seeker';
        anchor.download = `seeker-${safeName || seeker.id}-export.json`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        // Add small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error) {
        console.error(`Failed to export data for seeker ${seeker.name || seeker.id}:`, error);
        alert(`Failed to export data for seeker ${seeker.name || seeker.id}`);
      }
    }

    alert(`Export complete for ${selectedSeekers.length} seeker${selectedSeekers.length > 1 ? 's' : ''}`);
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">
            Seekers Management
          </h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Monitor and support seekers across the platform
          </p>
        </div>

        {/*<button
          type="button"
          className="inline-flex items-center gap-3 rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md transition-colors hover:bg-[#3C3450]"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFFCEF]/20 text-lg font-semibold">
            +
          </span>
          Add Seeker
        </button>*/}
      </header>

      <SeekerStats />

      <div className="flex flex-col gap-4 rounded-xl border border-[#2C2731] bg-[#2C2731] p-4 text-[#FEF6D6] shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="text-lg font-medium font-recoleta">All Seekers</div>
          {selectedIds.size > 0 && (
            <span className="text-sm text-[#FEF6D6]/70">
              ({selectedIds.size} selected)
            </span>
          )}
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          {selectedIds.size > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#FEF6D6] bg-[#2C2731] px-3 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
              >
                Bulk Actions
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showBulkActions && (
                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-[#2C2731] bg-[#FFFCEF] shadow-lg z-10">
                  <button
                    onClick={handleExportTable}
                    className="w-full px-4 py-2 text-left text-sm text-[#2C2731] hover:bg-[#F2EAC3] first:rounded-t-lg"
                  >
                    Export Table
                  </button>
                  <button
                    onClick={handleExportAllData}
                    className="w-full px-4 py-2 text-left text-sm text-[#2C2731] hover:bg-[#F2EAC3] last:rounded-b-lg"
                  >
                    Export All Data
                  </button>
                </div>
              )}
            </div>
          )}
          <input
            placeholder="Search seekers…"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
            className="w-full rounded-lg border border-[#FEF6D6] bg-[#FFFCEF] px-3 py-2.5 text-sm text-[#2C2731] placeholder-[#2C2731]/50 focus:border-[#F2EAC3] focus:outline-none focus:ring-2 focus:ring-[#F2EAC3] sm:w-64"
          />
          <button
            aria-label="Open filters"
            onClick={() => {
              setDraftStatus(status);
              setDraftOnboardingStatus(onboardingStatus);
              setDraftLifetimeMin(lifetimeMin);
              setDraftLifetimeMax(lifetimeMax);
              setShowFilters(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#FEF6D6] bg-[#2C2731] px-3 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[#FEF6D6]"
            >
              <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
            </svg>
            Filters
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-16">
          <div className="w-full max-w-xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
              <h2 className="text-lg font-medium font-recoleta">Filter Options</h2>
              <button
                onClick={() => setShowFilters(false)}
                className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-2 py-1 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                aria-label="Close filters"
              >
                ✕
              </button>
            </div>
            <div className="space-y-5 px-6 py-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2731]/80">
                  Status
                </label>
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value as any)}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "all" ? "All statuses" : opt === "no_match" ? "No Match" : opt.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2731]/80">
                  Onboarding Status
                </label>
                <select
                  value={draftOnboardingStatus}
                  onChange={(e) =>
                    setDraftOnboardingStatus(e.target.value as any)
                  }
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                >
                  {ONBOARDING_STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {ONBOARDING_STATUS_LABELS[opt]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2731]/80">
                  Lifetime Spend
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    placeholder="Min"
                    value={draftLifetimeMin}
                    onChange={(e) => setDraftLifetimeMin(e.target.value)}
                    className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  />
                  <span className="text-[#2C2731]/50">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={draftLifetimeMax}
                    onChange={(e) => setDraftLifetimeMax(e.target.value)}
                    className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  onClick={() => {
                    setDraftStatus("all");
                    setDraftOnboardingStatus("all");
                    setDraftLifetimeMin("");
                    setDraftLifetimeMax("");
                  }}
                  className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                >
                  Clear All
                </button>
                <button
                  onClick={() => {
                    setPage(1);
                    setStatus(draftStatus);
                    setOnboardingStatus(draftOnboardingStatus);
                    setLifetimeMin(draftLifetimeMin);
                    setLifetimeMax(draftLifetimeMax);
                    setShowFilters(false);
                  }}
                  className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#2C2731] text-[#FEF6D6]">
            <tr>
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-[#FEF6D6] text-[#2C2731] focus:ring-2 focus:ring-[#FEF6D6]"
                />
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-2">
                  Seeker
                  <SortIcon field="name" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center gap-2">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("onboardingStatus")}
              >
                <div className="flex items-center gap-2">
                  Onboarding
                  <SortIcon field="onboardingStatus" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Location
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("connectedGuides")}
              >
                <div className="flex items-center gap-2">
                  Active Guides
                  <SortIcon field="connectedGuides" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("lifetimeValue")}
              >
                <div className="flex items-center gap-2">
                  Lifetime Spend
                  <SortIcon field="lifetimeValue" />
                </div>
              </th>
              <th
                className="px-4 py-3 font-medium uppercase tracking-wide cursor-pointer hover:bg-[#3C3450] transition-colors"
                onClick={() => handleSort("joinedAt")}
              >
                <div className="flex items-center gap-2">
                  Joined
                  <SortIcon field="joinedAt" />
                </div>
              </th>
              <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C2731]/20">
            {loading && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-sm text-[#2C2731]/70"
                >
                  Loading…
                </td>
              </tr>
            )}

            {!loading && sortedItems.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-6 text-center text-sm text-[#2C2731]/70"
                >
                  No seekers found
                </td>
              </tr>
            )}

            {!loading &&
              sortedItems.map((seeker) => (
                <tr key={seeker.id} className="bg-[#FFFCEF] text-[#2C2731]">
                  <td className="px-4 py-4 align-top">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(seeker.id)}
                      onChange={() => toggleSelect(seeker.id)}
                      className="w-4 h-4 rounded border-[#2C2731] text-[#2C2731] focus:ring-2 focus:ring-[#2C2731]"
                    />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="font-medium text-[#2C2731] ph-mask">
                      {seeker.name || "—"}
                    </div>
                    <div className="text-xs text-[#2C2731]/70 ph-mask">
                      {seeker.email}
                    </div>
                    {seeker.phone && (
                      <div className="text-xs text-[#2C2731]/70 ph-mask">
                        {seeker.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <SeekerStatusBadge status={seeker.status} blocked={seeker.blocked} />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <OnboardingStatusBadge status={seeker.onboardingStatus} />
                  </td>
                  <td className="px-4 py-4 align-top text-[#2C2731]/70">
                    <SeekerLocationCell postcode={seeker.locationPostcode} />
                  </td>
                  <td className="px-4 py-4 align-top">
                    {seeker.connectedGuides}
                  </td>
                  <td className="px-4 py-4 align-top">
                    ${seeker.lifetimeValue.toLocaleString()}
                  </td>
                  <td className="px-4 py-4 align-top">
                    {new Date(seeker.joinedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 text-right align-top">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedSeeker(seeker)}
                        className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-3 py-2 text-xs font-semibold text-[#FEF6D6] transition-colors hover:bg-[#3C3450]"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => setDeleteTarget(seeker)}
                        className="rounded-lg border border-[#DC2626] bg-[#DC2626] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#B91C1C]"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 text-sm text-[#2C2731]/80 md:flex-row md:items-center md:justify-between">
        <div>Total: {res?.total ?? 0}</div>
        <div className="flex items-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page <= 1
                ? "cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40"
                : "bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]"
            }`}
          >
            Previous
          </button>
          <div className="px-2 text-sm text-[#2C2731]">
            Page {page} / {totalPages}
          </div>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className={`rounded-lg border border-[#2C2731] px-3 py-2 font-medium transition-colors ${
              page >= totalPages
                ? "cursor-not-allowed bg-[#F2EAC3] text-[#2C2731]/40"
                : "bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450]"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {selectedSeeker && (
        <SeekerDetailModal
          seeker={selectedSeeker}
          onClose={() => setSelectedSeeker(null)}
          onRefresh={() => refreshSamePage()}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          userId={deleteTarget.userId || ""}
          seekerId={deleteTarget.id}
          userName={deleteTarget.name || deleteTarget.email}
          role="seeker"
          isActive={false}
          hasActiveMatch={deleteTarget.connectedGuides > 0}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            refreshSamePage();
          }}
        />
      )}
    </div>
  );
}
